import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import type { Options } from "react-select";

import { graphql } from "../../gql";
import { filterValidCustomsClassifications } from "../utils/customsClassificationUtil";
import { getMaybeDateFromDateAndTime } from "../utils/dates/dateUtils";
import type { MinimalBooking } from "../utils/minimalBooking";
import {
  type CustomsClassification,
  type DropdownOption,
  transformCustomsClassificationOption,
} from "../utils/optionTransformers";
import type { CustomsClassificationsQuery_viewer_routes_byId_ukCustomsClassifications } from "./gql/CustomsClassificationsQuery";
import type { QueryResult } from "./types/QueryResult";

export const customsClassificationsQuery = graphql(/*GraphQL*/ `
  query CustomsClassificationsQuery($routeCode: ID!) {
    viewer {
      routes {
        byId(id: $routeCode) {
          id
          ukCustomsClassifications {
            id
            label
            value
            validFrom
            validTo
          }
        }
      }
    }
  }
`);

export const customsClassificationDropdownEmptyOption: DropdownOption<CustomsClassification> =
  {
    data: { label: "Select...", value: "null" },
    label: "Select...",
    value: "null",
  };

const getCustomsClassificationOptions = (
  ukCustomsClassifications:
    | CustomsClassificationsQuery_viewer_routes_byId_ukCustomsClassifications[]
    | undefined,
  departureDate: Date | undefined,
) => {
  if (!ukCustomsClassifications || !departureDate) {
    return [];
  }
  return filterValidCustomsClassifications(
    ukCustomsClassifications,
    departureDate,
  ).map((c) => transformCustomsClassificationOption(c));
};

export const transformCustomsClassificationsOptions = (
  ukCustomsClassifications:
    | CustomsClassificationsQuery_viewer_routes_byId_ukCustomsClassifications[]
    | undefined,
  departureDate: Date | undefined,
  includeEmptyOption: boolean = false,
): Options<DropdownOption<CustomsClassification>> => {
  const options: DropdownOption<CustomsClassification>[] = [];
  if (includeEmptyOption) {
    options.push(customsClassificationDropdownEmptyOption);
  }
  options.push(
    ...getCustomsClassificationOptions(ukCustomsClassifications, departureDate),
  );
  return options;
};

const useDepartureDateTime = (booking?: MinimalBooking) => {
  const departureDate = booking?.departureDate;
  const departureTime = booking?.departureTime;

  return useMemo(
    () => getMaybeDateFromDateAndTime(departureDate, departureTime),
    [departureDate, departureTime],
  );
};

export const useCustomsClassificationsOptions = (
  booking: MinimalBooking | undefined,
  skip: boolean = false,
): QueryResult<Options<DropdownOption<CustomsClassification>>> => {
  const { data, loading, error } = useQuery(customsClassificationsQuery, {
    variables: { routeCode: booking?.route?.code ?? "" },
    fetchPolicy: "cache-first",
    skip: skip || !booking?.route?.code,
  });

  const departureDate = useDepartureDateTime(booking);

  return useMemo(
    () => [
      transformCustomsClassificationsOptions(
        data?.viewer.routes.byId?.ukCustomsClassifications,
        departureDate,
      ),
      loading,
      error,
    ],
    [data, departureDate, error, loading],
  );
};
